import { useEffect, useState } from 'react';
import { api, normalizeVehicleDotacionFields } from '../../services/api';
import { personFormToAPI } from '../../utils/personFormAdapter';
import { useUserPrefs } from '../context/UserPrefs';

const parseBogotaDateStrict = (raw) => {
  if (raw instanceof Date) return raw;
  if (typeof raw === 'number') return new Date(raw);
  const s = String(raw || '').trim();
  if (!s) return new Date();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) {
    return new Date(s.replace(' ', 'T') + '-05:00');
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(s)) {
    return new Date(s + '-05:00');
  }
  return new Date(s);
};

const formatBogotaYmdHms = (val) => {
  const d = val instanceof Date ? val : (typeof val === 'number' ? new Date(val) : parseBogotaDateStrict(val));
  const parts = new Intl.DateTimeFormat('es-CO', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).formatToParts(d);
  const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}:${map.second}`;
};

export const useWarehouse = () => {
  const { currentUser } = useUserPrefs();
  const parseBogotaDate = (raw) => parseBogotaDateStrict(raw);
  const fmtDateTime = (raw) => {
    try { return formatBogotaYmdHms(raw); } catch (_) { return String(raw || ''); }
  };
  const isMeaningfulSerial = (val) => {
    const raw = String(val ?? '').trim();
    if (!raw) return false;
    const s = raw.replace(/\s+/g, ' ').trim();
    if (/^ns-\d+(?:-\d+)?$/i.test(s)) return false;
    if (/^(n\/a|na|no|sin serial|s\/n|sn|none|null|undefined|-|0+|no aplica)$/i.test(s)) return false;
    return true;
  };
  const computeRequiresSerial = (m) => {
    const nameL = String(m?.name || '').toLowerCase();
    const catL = String(m?.category || m?.type || '').toLowerCase();
    const codeU = String(m?.code || '').toUpperCase();
    if (codeU === 'OFC-003' || codeU === 'TEC-005') return false;
    const explicit = (m?.requires_serial === 1) || (m?.requires_serial === true) || (String(m?.requires_serial || '').toLowerCase() === 'true');
    if (explicit) return true;
    const hasSerial = isMeaningfulSerial(m?.serial);
    const isSimcard = /simcard/.test(nameL) || /simcard/.test(catL) || codeU === 'TEC-012';
    const isMonitor = /monitor/.test(nameL) || /monitor/.test(catL);
    const isPrinter = /(impresora|printer)/.test(nameL) || /(impresora|printer)/.test(catL);
    const isLaptop = /(portatil|portátil|laptop)/.test(nameL) || /(portatil|portátil|laptop)/.test(catL);
    const isCell = /(celular|telefono|teléfono|smartphone)/.test(nameL) || /(celular|telefono|teléfono|smartphone)/.test(catL);
    return hasSerial || isSimcard || isMonitor || isPrinter || isLaptop || isCell;
  };
  const normalizeWarehouseId = (v) => {
    const s = String(v || '').toLowerCase().trim();
    const noAcc = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const slug = noAcc.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (/^popayan/.test(slug)) return 'popayan';
    if (/^santander/.test(slug) || /quilichao/.test(slug)) return 'santander';
    if (/^(c054|lv).*centro/.test(slug)) return 'lv-centro';
    if (/^(c086|lv).*norte/.test(slug)) return 'lv-norte';
    return slug || s;
  };
  // Materiales: persistent storage
  const initialMaterials = (() => {
    try {
      const raw = localStorage.getItem('senerfi_materials');
      if (raw) {
        const parsed = JSON.parse(raw);
        const arr = Array.isArray(parsed) ? parsed : (parsed ? [parsed] : []);
        return arr.map((m) => {
          const next = { ...(m || {}) };
          next.serial = next.serial || '';
          next.requiresSerial = computeRequiresSerial(next);
          return next;
        });
      }
    } catch (e) {}
    return [];
  })();
  const [materials, setMaterials] = useState(initialMaterials);
  const persistMaterials = (next) => {
    setMaterials(next);
    try { localStorage.setItem('senerfi_materials', JSON.stringify(next)); } catch (e) {}
  };

  // Marca-item: elementos de marca asociados a códigos de materiales (persistentes)
  const initialBrandItems = (() => {
    try {
      const raw = localStorage.getItem('senerfi_brand_items');
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return [];
  })();
  const [brandItems, setBrandItems] = useState(initialBrandItems);
  const persistBrandItems = (next) => {
    setBrandItems(next);
    try { localStorage.setItem('senerfi_brand_items', JSON.stringify(next)); } catch (e) {}
  };

  const initialPeople = (() => {
    try {
      const raw = localStorage.getItem('senerfi_people');
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return [];
  })();
  const [people, setPeople] = useState(initialPeople);

  const persistPeople = (next) => {
    setPeople(next);
    try {
      localStorage.setItem('senerfi_people', JSON.stringify(next));
    } catch (e) {}
  };

  // Movimientos: solo en memoria. El Histórico consulta la API en vivo;
  // no se hidrata ni se persiste senerfi_movements (impide ver datos frescos).
  const [movements, setMovements] = useState([]);
  const persistMovements = (next) => {
    setMovements(next);
  };
  const [movementsRefreshKey, setMovementsRefreshKey] = useState(0);
  const bumpMovementsRefresh = () => setMovementsRefreshKey((n) => n + 1);

  useEffect(() => {
    try { localStorage.removeItem('senerfi_movements'); } catch (e) {}
  }, []);

  // Asignaciones: persistentes
  const initialAssignments = (() => {
    try {
      const raw = localStorage.getItem('senerfi_assignments');
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return [];
  })();
  const [assignments, setAssignments] = useState(initialAssignments);
  const persistAssignments = (next) => {
    setAssignments(next);
    try { localStorage.setItem('senerfi_assignments', JSON.stringify(next)); } catch (e) {}
  };

  const [warehouses, setWarehouses] = useState([
    { id: 'lv-centro', name: 'C054 Lv Zona Centro' },
    { id: 'lv-norte', name: 'C086 Lv Zona Norte' },
    { id: 'popayan', name: 'Popayán' },
    { id: 'santander', name: 'Santander de Quilichao' },
  ]);

  const addWarehouse = async (data) => {
    const nm = String(data?.name || '').trim();
    const idInput = String(data?.id || data?.code || '').trim();
    const idStr = normalizeWarehouseId(idInput || nm);
    const parentId = data?.parent_id ? String(data.parent_id).trim() : null;
    const consecutivoPrefix = data?.consecutivo_prefix ? String(data.consecutivo_prefix).trim() : null;
    try {
      await api.warehouses.upsert({ id: idStr, code: idStr, name: nm, parent_id: parentId, consecutivo_prefix: consecutivoPrefix });
    } catch (_) {}
    const seen = new Set();
    const next = [...(warehouses || []), { id: idStr, name: nm, parent_id: parentId, consecutivo_prefix: consecutivoPrefix }]
      .filter((w) => { const k = String(w.id); if (seen.has(k)) return false; seen.add(k); return true; })
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    setWarehouses(next);
    return { id: idStr, name: nm, parent_id: parentId, consecutivo_prefix: consecutivoPrefix };
  };

  const updateWarehouse = async (data) => {
    const idStr = String(data?.id || '').trim();
    const nm = String(data?.name || '').trim();
    if (!idStr || !nm) return null;
    const parentId = data?.parent_id ? String(data.parent_id).trim() : null;
    const consecutivoPrefix = data?.consecutivo_prefix ? String(data.consecutivo_prefix).trim() : null;
    try { await api.warehouses.upsert({ id: idStr, name: nm, parent_id: parentId, consecutivo_prefix: consecutivoPrefix }); } catch (_) {}
    const next = (warehouses || []).map((w) => (String(w.id) === idStr ? { ...w, name: nm, parent_id: parentId, consecutivo_prefix: consecutivoPrefix } : w))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    setWarehouses(next);
    return { id: idStr, name: nm, parent_id: parentId, consecutivo_prefix: consecutivoPrefix };
  };

  const deleteWarehouse = async (id) => {
    const idStr = String(id || '').trim();
    if (!idStr) return false;
    try { await api.warehouses.delete({ id: idStr, action: 'delete' }); } catch (_) {}
    const next = (warehouses || []).filter((w) => String(w.id) !== idStr);
    setWarehouses(next);
    return true;
  };

  // Carga inicial desde backend (materiales, stock, marcas, personas, movimientos, asignaciones, bodegas)
  // Carga inicial única del hook desde backend y caché local.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      try {
        // Las siguientes cargas (bodegas+materiales+stock, ítems de marca,
        // personas+RRHH, movimientos y asignaciones) son independientes entre
        // sí a nivel de PETICIÓN, aunque su PROCESAMIENTO posterior sí depende
        // unas de otras. Antes se esperaban en serie (una decena de llamadas
        // paginadas de movimientos, más materiales, más stock, etc., todo uno
        // detrás de otro), lo cual hacía la carga inicial del módulo Almacén
        // muy lenta. Se disparan en paralelo con Promise.all y el
        // procesamiento que depende de varias (movimientos->toUi necesita
        // ítems de marca y personas; asignaciones necesita movimientos) se
        // hace después, una vez resueltas todas.

        const loadWarehousesMaterialsStock = async () => {
          let apiWhList = [];
          // Warehouses
          try {
            let list = [];
            try {
              const wh = await api.warehouses.list({ limit: 200, order: 'asc' });
              list = Array.isArray(wh?.data) ? wh.data : [];
            } catch (_) {}
            if (!list.length) {
              try {
                const wh2 = await api.warehouses.list({ limit: 200, order: 'asc', include_deleted: 1 });
                list = Array.isArray(wh2?.data) ? wh2.data : [];
              } catch (_) {}
            }
            if (list.length) {
              apiWhList = list.map((w) => {
                const idStr = normalizeWarehouseId(w.id || w.code || w.name);
                const nameOut = String(w.name || idStr || '').trim() || idStr;
                const parentId = w.parent_id ? normalizeWarehouseId(w.parent_id) : null;
                return { id: idStr, name: nameOut, parent_id: parentId, consecutivo_prefix: w.consecutivo_prefix || null };
              });
            }
          } catch (_) {}

          // Materials (paginado para asegurar cobertura completa)
          let matsRaw = [];
          try {
            const toArr = (resp) => {
              const d = resp?.data ?? resp;
              if (Array.isArray(d)) return d;
              if (Array.isArray(d?.rows)) return d.rows;
              if (Array.isArray(d?.list)) return d.list;
              if (Array.isArray(resp?.rows)) return resp.rows;
              if (Array.isArray(resp?.list)) return resp.list;
              if (d && typeof d === 'object' && Object.keys(d).length > 0) return [d];
              return [];
            };
            const chunk = 2000;
            let offset = 0;
            while (true) {
              const mRes = await api.materials.get({ limit: chunk, offset });
              const page = toArr(mRes);
              if (!page.length) break;
              matsRaw = matsRaw.concat(page);
              if (page.length < chunk) break;
              offset += chunk;
            }
          } catch (_) {
            const fallback = await api.materials.get({ limit: 5000, include_all: 1 });
            matsRaw = Array.isArray(fallback?.data) ? fallback.data : (fallback?.data ? [fallback.data] : []);
          }
          // Deduplicar solo por id (preferente) o por code cuando no haya id
          const seen = new Set();
          matsRaw = matsRaw.filter((m) => {
            const idStr = String(m.id ?? '').trim();
            const codeStr = String(m.code ?? '').trim();
            const key = idStr ? `id:${idStr}` : (codeStr ? `code:${codeStr}` : '');
            if (!key) return false;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

          let mats = matsRaw.map(m => ({
            id: m.id,
            code: m.code,
            name: m.name,
            brand: m.brand || '',
            category: m.category || '',
            // Mostrar tipo desde categoría; si falta, usar 'Material' por defecto
            type: m.category || 'Material',
            unit: m.unit || 'Unidad',
            stockMin: Number(m.stock_min ?? m.stockMin ?? 0) || 0,
            stockActual: 0,
            stockMax: Number(m.stock_max ?? m.stockMax ?? 0) || 0,
            location: m.location || '',
            status: m.status || 'Activo',
            price: m.price ? Number(m.price) : 0,
            // Costeo real (Fase 2): costo promedio ponderado calculado a
            // partir de entradas con precio de OC — null hasta que el
            // material reciba su primera entrada costeada.
            avgCost: (m.avg_cost !== null && m.avg_cost !== undefined && m.avg_cost !== '') ? Number(m.avg_cost) : null,
            dateRegistered: new Date().toISOString().split('T')[0],
            requiresExpire: (m.requires_expire === 1) || (String(m.requires_expire).toLowerCase() === 'true') || (String(m.requiere_vencimiento).toLowerCase() === 'si'),
            warranty_until: m.warranty_until || m.vencimiento || m.expiration_date || '',
            requiresSerial: computeRequiresSerial(m),
            serial: m.serial || '',
            ...normalizeVehicleDotacionFields(m),
            stockByWarehouse: {}
          }));

          // Stock por bodega (paginado para traer todo)
          try {
            let rows = [];
            const chunk = 1000;
            let offset = 0;
            while (true) {
              const sbwRes = await api.stockByWarehouse.get({ limit: chunk, offset });
              const page = Array.isArray(sbwRes?.data) ? sbwRes.data : [];
              if (!page.length) break;
              rows = rows.concat(page);
              if (page.length < chunk) break;
              offset += chunk;
            }
            const byId = new Map();
            mats.forEach(m => byId.set(Number(m.id), m));
            const missingCodes = new Set();
            rows.forEach(r => {
              let mat = byId.get(Number(r.material_id));
              if (!mat) {
                mat = {
                  id: Number(r.material_id),
                  code: String(r.material_code || r.material_id || ''),
                  name: String(r.material_name || r.material_code || '').trim(),
                  brand: '',
                  category: '',
                  type: '',
                  unit: 'Unidad',
                  stockMin: 0,
                  stockActual: 0,
                  stockMax: 0,
                  location: '',
                  status: 'Activo',
                  price: 0,
                  avgCost: null,
                  dateRegistered: new Date().toISOString().split('T')[0],
                  requiresSerial: false,
                  serial: '',
                  stockByWarehouse: {}
                };
                mats.push(mat);
                byId.set(mat.id, mat);
                if (mat.code) missingCodes.add(mat.code);
              }
              const whId = normalizeWarehouseId(r.warehouse_id);
              const qty = Number(r.stock || 0);
              mat.stockByWarehouse = { ...(mat.stockByWarehouse || {}), [whId]: qty };
            });
            // Intentar completar datos de materiales faltantes desde la API por código
            for (const code of Array.from(missingCodes)) {
              try {
                const resp = await api.materials.get({ code, limit: 1, include_deleted: 1 });
                const arr = Array.isArray(resp?.data) ? resp.data : (resp?.data ? [resp.data] : []);
                const m = arr[0];
                if (m) {
                  // Buscar el material placeholder creado por material_id del stock
                  const mat = mats.find(x => String(x.code) === String(code));
                  if (mat) {
                    mat.name = m.name || mat.name;
                    mat.brand = m.brand || mat.brand;
                    mat.category = m.category || mat.category;
                    mat.type = m.type || m.category || mat.type || '';
                    mat.unit = m.unit || mat.unit;
                    mat.stockMin = Number(m.stock_min ?? m.stockMin ?? mat.stockMin ?? 0) || 0;
                    mat.stockMax = Number(m.stock_max ?? m.stockMax ?? mat.stockMax ?? 0) || 0;
                    mat.status = m.status || mat.status;
                    mat.price = m.price ? Number(m.price) : mat.price;
                    mat.serial = m.serial || mat.serial;
                    mat.requires_serial = (m.requires_serial === 1) || (String(m.requires_serial).toLowerCase() === 'true') ? 1 : 0;
                    mat.requiresExpire = (m.requires_expire === 1) || (String(m.requires_expire).toLowerCase() === 'true') || (String(m.requiere_vencimiento).toLowerCase() === 'si');
                    mat.warranty_until = m.warranty_until || m.vencimiento || m.expiration_date || mat.warranty_until || '';
                    mat.requiresSerial = computeRequiresSerial(mat);
                    Object.assign(mat, normalizeVehicleDotacionFields(m));
                  }
                }
              } catch (_) {}
            }
            mats = mats.map(m => ({
              ...m,
              stockActual: Object.values(m.stockByWarehouse || {}).reduce((acc, v) => acc + (parseInt(v) || 0), 0)
            }));
            // Unir bodegas de API y las detectadas desde stock
            const fromStockIds = Array.from(new Set(rows.map(r => normalizeWarehouseId(r.warehouse_id))));
            const apiIds = Array.from(new Set(apiWhList.map(w => String(w.id))));
            const unionIds = new Set([...apiIds, ...fromStockIds]);
            if (unionIds.size === 0) {
              unionIds.add('popayan');
              unionIds.add('santander');
            }
            const nameMap = new Map(apiWhList.map(w => [String(w.id), w.name]));
            const parentMap = new Map(apiWhList.map(w => [String(w.id), w.parent_id || null]));
            const prefixMap = new Map(apiWhList.map(w => [String(w.id), w.consecutivo_prefix || null]));
            const finalWh = Array.from(unionIds).map((id) => {
              const low = id.toLowerCase();
              const apiName = nameMap.get(id) || nameMap.get(low);
              const name = apiName || (low.includes('popay') ? 'Popayán' : (low.includes('santander') || low.includes('quilichao') ? 'Santander de Quilichao' : id));
              const parentId = parentMap.get(id) || parentMap.get(low) || null;
              const consecutivoPrefix = prefixMap.get(id) || prefixMap.get(low) || null;
              return { id: low, name, parent_id: parentId, consecutivo_prefix: consecutivoPrefix };
            });
            // Ordenar por nombre y eliminar duplicados por id
            const seenWh = new Set();
            const sorted = finalWh
              .filter((w) => { const k = String(w.id); if (seenWh.has(k)) return false; seenWh.add(k); return true; })
              .sort((a, b) => String(a.name).localeCompare(String(b.name)));
            setWarehouses(sorted);
          } catch (_) {}
          const parseCode = (c) => {
            const s = String(c || '').trim();
            const m = s.match(/^([A-Za-z]+[- ]?)(\d+)(.*)$/);
            if (m) return { prefix: m[1].toUpperCase(), num: parseInt(m[2], 10), rest: m[3] || '' };
            const nMatch = s.match(/(\d+)(?!.*\d)/);
            const num = nMatch ? parseInt(nMatch[1], 10) : null;
            const prefix = s.replace(/\d+$/, '').toUpperCase();
            return { prefix, num, rest: '' };
          };
          const sortedMats = [...mats].sort((a, b) => {
            const pa = parseCode(a.code || a.name || '');
            const pb = parseCode(b.code || b.name || '');
            if (pa.prefix !== pb.prefix) return pa.prefix.localeCompare(pb.prefix);
            if (pa.num !== null && pb.num !== null && pa.num !== pb.num) return pa.num - pb.num;
            return String(a.code || a.name || '').localeCompare(String(b.code || b.name || ''));
          });
          persistMaterials(sortedMats);
        };

        const loadBrandItems = async () => {
          // Antes limit:1000 truncaba silenciosamente esta lista (más de
          // 5.000 brand_items reales en producción a sep/2026), dejando
          // fuera del caché de sugerencias de marca cualquier unidad no
          // incluida en las primeras 1000 filas devueltas. brand_items.php
          // admite hasta 20000 por página, suficiente para traerlas todas
          // en una sola llamada.
          const biRes = await api.brandItems.get({ limit: 20000 });
          const bis = Array.isArray(biRes?.data)
            ? biRes.data.map(b => ({
                id: b.id,
                materialCode: b.material_code || '',
                materialType: b.material_type || '',
                name: b.brand || '',
                serial: b.serial || '',
                status: b.status || 'Activo'
              }))
            : [];
          persistBrandItems(bis);
          return bis;
        };

        const loadPeople = async () => {
        // People (incluye campos RRHH integrados en people). clean=1 mezcla extra_json y limpia vacíos.
        const pRes = await api.people.get({ limit: 1000, clean: 1 });
        const persons = Array.isArray(pRes?.data)
          ? pRes.data.map(p => ({
              id: p.id,
              _dbId: p._dbId ?? null,
              dni: p.dni || p.cedula || p.documento_identidad || p.documento || p.doc || p.id || '',
              name: p.name || p.nombre || p.nombre_completo || p.fullname || '',
              // Backend puede retornar role (base) y/o position (extra). Preferir position.
              position: (p.position ?? p.role) || '',
              operationalRole: p.operationalRole || p.operational_role || '',
              area: p.area || '',
              phone: p.phone || '',
              email: p.email || '',
              status: p.status || 'Activo',
              birthDate: p.birthDate || p.birth_date || '',
              address: p.address || '',
              eps: p.eps || '',
              pensionFund: p.pensionFund || p.pension_fund || '',
              bloodType: p.bloodType || p.blood_type || '',
              education: p.education || p.education_level || '',
              civilStatus: p.civilStatus || p.marital_status || '',
              photo: p.photo || p.photo_url || '',
              signature: p.signature || p.signature_url || '',
              notes: p.notes || '',
              afp: p.afp || null,
              idIssuePlace: p.idIssuePlace || '',
              category: p.category || '',
              appliesExtras: p.appliesExtras || '',
              conteCategories: Array.isArray(p.conteCategories) ? p.conteCategories : [],
              professionalCouncils: Array.isArray(p.professionalCouncils) ? p.professionalCouncils : [],
              workExperience: Array.isArray(p.workExperience) ? p.workExperience : [],
              vaccineDoses: (p.vaccineDoses && typeof p.vaccineDoses === 'object') ? p.vaccineDoses : {},
              firstResponder: (p.firstResponder && typeof p.firstResponder === 'object') ? p.firstResponder : {},
              secondResponder: (p.secondResponder && typeof p.secondResponder === 'object') ? p.secondResponder : {},
              // Extras integrados desde extra_json
              hireDate: p.hireDate || p.entryDate || '',
              contractDate: p.contractDate || '',
              contractNumber: p.contractNumber || '',
              currentContractModality: p.currentContractModality || '',
              otrosiDates: Array.isArray(p.otrosiDates) ? p.otrosiDates : (p.otrosiDate ? [p.otrosiDate] : []),
              emoDate: p.emoDate || '',
              nextEmoDate: p.nextEmoDate || '',
              clientValidation: p.clientValidation || '',
              arlRiskPercent: p.arlRiskPercent ?? null,
              contractType: p.contractType || p.contract_type || undefined,
              immediateBossId: p.immediateBossId ?? null,
              attachments: Array.isArray(p.attachments) ? p.attachments : (p.attachments ? [p.attachments] : []),
              documents: (p.documents && typeof p.documents === 'object') ? p.documents : {},
              compensationFund: p.compensationFund || '',
              drivingLicense: p.drivingLicense || '',
              drivingLicenseExpiry: p.drivingLicenseExpiry || '',
              drivingLicenses: Array.isArray(p.drivingLicenses) ? p.drivingLicenses : (p.drivingLicense ? [{ category: p.drivingLicense, expiry: p.drivingLicenseExpiry || '' }] : []),
              allergies: p.allergies || '',
              allergyType: p.allergyType || '',
              birthCity: p.birthCity || '',
              neighborhood: p.neighborhood || '',
              municipality: p.municipality || '',
              department: p.department || '',
            }))
          : [];
        let mergedPeople = persons;
        // Fusionar datos de RRHH (hr_employees) para obtener contract_type, hire_date, etc.
        try {
          const heRes = await api.hr.employees.list({ limit: 1000 });
          const heRows = Array.isArray(heRes?.data) ? heRes.data : [];
          const byDni = new Map(heRows.filter(r => r?.dni).map(r => [String(r.dni), r]));
          const byId = new Map(heRows.filter(r => r?.person_id).map(r => [String(r.person_id), r]));
          const merged = persons.map(p => {
            const hr = byDni.get(String(p.dni || p.id)) || (p._dbId ? byId.get(String(p._dbId)) : undefined) || byId.get(String(p.id));
            if (!hr) return p;
            return {
              ...p,
              position: hr.position || p.position,
              operationalRole: p.operationalRole || hr.operational_role || p.operational_role || '',
              area: hr.department || p.area,
              hireDate: hr.hire_date || p.hireDate,
              contractType: hr.contract_type || p.contractType,
              currentContractModality: p.currentContractModality || hr.contract_type || p.currentContractModality,
              status: hr.status || p.status,
            };
          });
          mergedPeople = merged;
          persistPeople(merged);
        } catch (_) {
          // Si falla RRHH, persistir lo cargado de people
          mergedPeople = persons;
          persistPeople(persons);
        }
          return mergedPeople;
        };

        const loadMovementsRaw = async () => {
          const mvLimit = 1000;
          let mvOffset = 0;
          let mvRows = [];
          let lastFirstId = null;
          while (true) {
            const mvRes = await api.movements.get({ limit: mvLimit, offset: mvOffset, order: 'desc' });
            const page = Array.isArray(mvRes?.data) ? mvRes.data : [];
            if (!page.length) break;
            const pageFirstId = page[0]?.id ?? null;
            if (mvOffset > 0 && pageFirstId && pageFirstId === lastFirstId) break;
            lastFirstId = pageFirstId;
            mvRows = mvRows.concat(page);
            if (page.length < mvLimit) break;
            mvOffset += mvLimit;
            if (mvRows.length >= 50000) break;
          }
          return mvRows;
        };

        const loadAssignmentsRaw = async () => {
          const pageSize = 1000;
          let allAsg = [];
          for (let off = 0; ; off += pageSize) {
            const resp = await api.assignments.get({ limit: pageSize, offset: off, order: 'asc' });
            const rows = Array.isArray(resp?.data) ? resp.data : [];
            if (rows.length === 0) break;
            allAsg = allAsg.concat(rows);
            if (rows.length < pageSize) break;
          }
          return allAsg;
        };

        const [, bis, mergedPeople, mvRows, asgRows] = await Promise.all([
          loadWarehousesMaterialsStock(),
          loadBrandItems(),
          loadPeople(),
          loadMovementsRaw(),
          loadAssignmentsRaw(),
        ]);

        const toUi = (mov) => {
          const bi = bis.find(b => Number(b.id) === Number(mov.brand_item_id));
          let typed = String(mov.type || '').toLowerCase();
          // Normalizar tipos con tilde o variaciones
          if (typed === 'préstamo') typed = 'prestamo';
          if (typed === 'asignación') typed = 'asignacion';
          if (typed === 'devolución') typed = 'devolucion';

          // Fallback: detect consumible via notes if type is generic salida
          if (typed === 'salida' && String(mov.notes || '').toLowerCase().includes('consumible')) {
            typed = 'consumible';
          }
          const typeUi = typed === 'entrada' ? 'Entrada' : typed === 'asignacion' ? 'Asignación' : typed === 'devolucion' ? 'Devolución' : typed === 'prestamo' ? 'Préstamo' : typed === 'consumible' ? 'Consumible' : 'Salida';
          const nameNorm = String(mov.requester_name || '').trim().toLowerCase();
          const peopleList = Array.isArray(mergedPeople) ? mergedPeople : [];
          const personMatch = Array.isArray(peopleList) ? peopleList.find(p => String(p.name || '').trim().toLowerCase() === nameNorm) : null;
          const dniResolved = mov.requester_dni || mov.dni || personMatch?.dni || personMatch?.id || null;
          const unitResolved = mov.material_unit || (materials.find(mm => Number(mm.id) === Number(mov.material_id))?.unit) || 'Unidad';
          return {
            id: mov.id,
            type: typeUi,
            materialId: mov.material_id,
            materialName: mov.material_name || '',
            materialCode: mov.material_code || '',
            materialUnit: unitResolved,
            unit: unitResolved,
            responsibleName: mov.requester_name || '',
            responsibleDoc: dniResolved,
            quantity: mov.quantity,
            date: mov.created_at ? fmtDateTime(mov.created_at) : formatBogotaYmdHms(new Date()),
            ts: mov.created_at ? parseBogotaDate(mov.created_at).getTime() : Date.now(),
            user: mov.performed_by_name || mov.performed_by_email || '',
            observations: mov.notes || '',
            entryRef: mov.entry_ref || '',
            status: 'Completado',
            serial: String(mov.serial || '').trim() || bi?.serial || '',
            brandName: String(mov.brand || '').trim() || bi?.brand || bi?.name || '',
            warehouseId: mov.warehouse_id || '',
            ocId: mov.oc_id ? Number(mov.oc_id) : null,
            ocCode: String(mov.oc_code || '').trim(),
            unitCost: (mov.unit_cost !== null && mov.unit_cost !== undefined && Number(mov.unit_cost) > 0)
              ? Number(mov.unit_cost)
              : null,
            ocProvider: String(mov.oc_provider || '').trim(),
            ocProviderNit: String(mov.oc_provider_nit || '').trim(),
          };
        };
        persistMovements(mvRows.map(toUi));

        // Assignments (sin serial en backend; se mantiene solo datos base; ya cargadas en paralelo arriba vía loadAssignmentsRaw)
        // NOTA: a.quantity ya viene neto de devoluciones previas — el
        // backend decrementa (o elimina) la fila de assignments dentro de
        // la misma transacción que crea el movimiento de devolución (ver
        // server/api/movements.php, applyReturnToRow). Antes, aquí se volvía
        // a restar el total histórico de devoluciones sobre a.quantity,
        // descontándolo DOS veces y dejando en el estado local una cantidad
        // asignada menor a la real — eso hacía que "Cantidad a Devolver"
        // se rechazara como mayor a la asignada aunque coincidiera
        // exactamente con lo que mostraba el formulario.
        const asgUi = asgRows.map(a => ({
          id: a.id,
          assignmentDbId: a.id,
          materialId: a.material_id,
          materialName: a.material_name || '',
          materialCode: a.material_code || '',
          materialCategory: a.material_category || a.materialCategory || '',
          responsibleDoc: a.person_dni || a.person_id,
          responsibleName: a.person_name || String(a.person_dni || a.person_id || ''),
          quantity: Number(a.quantity) || 0,
          assignDate: a.created_at ? String(a.created_at).slice(0, 10) : new Date().toISOString().slice(0, 10),
          serial: String(a.serial || '').trim(),
          brandName: String(a.brand || '').trim(),
          hasPeripherals: String(a.serial || '').trim() ? 'Sí' : 'No',
          status: (Number(a.quantity) || 0) <= 0 ? 'Devuelto' : 'Asignado',
          observations: a.notes || '',
          evidencePhotos: [],
          warehouseId: a.warehouse_id || ''
        }));
        persistAssignments(asgUi);
      } catch (err) {
        console.error('Error cargando datos iniciales de almacén', err);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Material Operations
  const addMaterial = async (material) => {
    try {
      const requires_serial = material.hasSerial === 'Sí' ? 1 : 0;
      const payload = {
        code: material.code,
        name: material.name,
        brand: material.brand || null,
        // Mapear ‘type’ del formulario a ‘category’ para backend
        category: material.category || material.type || null,
        unit: material.unit || null,
        location: material.location || null,
        status: material.status || 'Activo',
        price: material.price || null,
        stock_min: material.stockMin || 0,
        stock_max: material.stockMax || 0,
        serial: (material.serial && String(material.serial).trim() !== '') ? material.serial : null,
        requires_serial,
        ...normalizeVehicleDotacionFields(material),
      };
      await api.materials.upsert(payload);
      // recuperar el material por código para obtener id
      const got = await api.materials.get({ code: material.code });
      const m = got?.data ? got.data : null;
      const ui = m ? {
        id: m.id,
        code: m.code,
        name: m.name,
        brand: m.brand || '',
        category: m.category || '',
        // Asegurar que el tipo refleje lo elegido si la API no devolviera categoría
        type: m.category || material.type || 'Material',
        unit: m.unit || 'Unidad',
        stockMin: Number(m.stock_min ?? m.stockMin ?? material.stockMin ?? 0) || 0,
        stockActual: 0,
        stockMax: Number(m.stock_max ?? m.stockMax ?? material.stockMax ?? 0) || 0,
        location: m.location || '',
        status: m.status || 'Activo',
        price: m.price ? Number(m.price) : 0,
        avgCost: (m.avg_cost !== null && m.avg_cost !== undefined && m.avg_cost !== '') ? Number(m.avg_cost) : null,
        dateRegistered: new Date().toISOString().split('T')[0],
        requires_serial: (m.requires_serial === 1) || (String(m.requires_serial).toLowerCase() === 'true') ? 1 : 0,
        requiresSerial: computeRequiresSerial({ ...m, requires_serial: (m.requires_serial === 1) || (String(m.requires_serial).toLowerCase() === 'true') ? 1 : 0 }),
        serial: m.serial || '',
        ...normalizeVehicleDotacionFields(m),
        stockByWarehouse: {}
      } : { ...material, id: Date.now(), dateRegistered: new Date().toISOString().split('T')[0], requires_serial, requiresSerial: !!requires_serial, ...normalizeVehicleDotacionFields(material) };
      const next = [...materials, ui];
      persistMaterials(next);
      return ui;
    } catch (e) {
      console.error('addMaterial error', e);
      // fallback local
      const newMaterial = { ...material, id: Date.now(), dateRegistered: new Date().toISOString().split('T')[0] };
      const next = [...materials, newMaterial];
      persistMaterials(next);
      return newMaterial;
    }
  };

  const updateMaterial = async (id, updatedMaterial) => {
    try {
      const m = materials.find(x => x.id === id);
      if (m) {
        const requires_serial = updatedMaterial.hasSerial === 'Sí' ? 1 : 0;
        const nextSerial = (requires_serial === 1)
          ? ((updatedMaterial.serial && String(updatedMaterial.serial).trim() !== '') ? updatedMaterial.serial : (m.serial || null))
          : null;
        await api.materials.upsert({
          code: m.code,
          name: updatedMaterial.name || m.name,
          brand: updatedMaterial.brand ?? m.brand,
          // Asegurar que ‘type’ también se mapea a ‘category’ en actualización
          category: updatedMaterial.category ?? updatedMaterial.type ?? m.category,
          unit: updatedMaterial.unit ?? m.unit,
          location: updatedMaterial.location ?? m.location,
          status: updatedMaterial.status ?? m.status,
          price: updatedMaterial.price ?? m.price,
          stock_min: updatedMaterial.stockMin ?? m.stockMin ?? 0,
          stock_max: updatedMaterial.stockMax ?? m.stockMax ?? 0,
          serial: nextSerial,
          requires_serial,
          ...normalizeVehicleDotacionFields({
            requires_vehicle: updatedMaterial.requires_vehicle ?? m.requires_vehicle,
            dotacion_category: updatedMaterial.dotacion_category ?? m.dotacion_category,
          }),
        });
      }
    } catch (e) {
      console.error('updateMaterial error', e);
    }
    const next = materials.map(m => m.id === id ? {
      ...updatedMaterial,
      ...normalizeVehicleDotacionFields({
        requires_vehicle: updatedMaterial.requires_vehicle ?? m.requires_vehicle,
        dotacion_category: updatedMaterial.dotacion_category ?? m.dotacion_category,
      }),
      id,
      dateRegistered: m.dateRegistered,
    } : m);
    persistMaterials(next);
  };

  const deleteMaterial = async (id) => {
    const m = materials.find(x => x.id === id);
    if (m) {
      try {
        await api.materials.delete({ id: m.id, code: m.code });
      } catch (e) {
        console.error('deleteMaterial failed', e);
        return;
      }
    }
    const next = materials.filter(m => m.id !== id);
    persistMaterials(next);
  };

  // BrandItem Operations
  const addBrandItem = async (item) => {
    try {
      const res = await api.brandItems.upsert({
        material_code: item.materialCode,
        material_type: item.materialType || null,
        brand: item.name || null,
        model: item.model || null,
        serial: item.serial || null,
        status: item.status || 'Activo'
      });
      const id = res?.id || Date.now();
      const newItem = { ...item, id };
      const next = [...brandItems, newItem];
      persistBrandItems(next);
      return newItem;
    } catch (e) {
      console.error('addBrandItem error', e);
      const newItem = { ...item, id: Date.now() };
      const next = [...brandItems, newItem];
      persistBrandItems(next);
      return newItem;
    }
  };

  const updateBrandItem = async (id, updatedItem) => {
    try {
      await api.brandItems.upsert({
        id,
        material_code: updatedItem.materialCode,
        material_type: updatedItem.materialType ?? null,
        brand: updatedItem.name ?? null,
        model: updatedItem.model ?? null,
        serial: updatedItem.serial ?? null,
        status: updatedItem.status ?? 'Activo'
      });
    } catch (e) { console.error('updateBrandItem error', e); }
    const next = brandItems.map(b => b.id === id ? { ...updatedItem, id } : b);
    persistBrandItems(next);
  };

  const deleteBrandItem = async (id) => {
    try { await api.brandItems.delete({ id }); } catch (e) { console.error('deleteBrandItem error', e); }
    const next = brandItems.filter(b => b.id !== id);
    persistBrandItems(next);
  };

  const searchMovements = async ({ startDate, endDate }) => {
    try {
      const toUi = (mov) => {
        const bi = brandItems.find(b => Number(b.id) === Number(mov.brand_item_id));
        let typed = String(mov.type || '').toLowerCase();
        if (typed === 'préstamo') typed = 'prestamo';
        if (typed === 'asignación') typed = 'asignacion';
        if (typed === 'devolución') typed = 'devolucion';
        if (typed === 'salida' && String(mov.notes || '').toLowerCase().includes('consumible')) {
          typed = 'consumible';
        }
        const typeUi = typed === 'entrada' ? 'Entrada' : typed === 'asignacion' ? 'Asignación' : typed === 'devolucion' ? 'Devolución' : typed === 'prestamo' ? 'Préstamo' : typed === 'consumible' ? 'Consumible' : 'Salida';
        
        const nameNorm = String(mov.requester_name || '').trim().toLowerCase();
        const personMatch = people.find(p => String(p.name || '').trim().toLowerCase() === nameNorm);
        const dniResolved = mov.requester_dni || mov.dni || personMatch?.dni || personMatch?.id || null;
        
        const unitResolved = mov.material_unit || (materials.find(mm => Number(mm.id) === Number(mov.material_id))?.unit) || 'Unidad';

        return {
          id: mov.id,
          type: typeUi,
          materialId: mov.material_id,
          materialName: mov.material_name || '',
          materialCode: mov.material_code || '',
          materialUnit: unitResolved,
          unit: unitResolved,
          responsibleName: mov.requester_name || '',
          responsibleDoc: dniResolved,
          quantity: mov.quantity,
          date: mov.created_at ? fmtDateTime(mov.created_at) : formatBogotaYmdHms(new Date()),
          ts: mov.created_at ? parseBogotaDate(mov.created_at).getTime() : Date.now(),
          user: mov.performed_by_name || mov.performed_by_email || '',
          observations: mov.notes || '',
          entryRef: mov.entry_ref || '',
          status: 'Completado',
          serial: bi?.serial || '',
          brandName: bi?.name || '',
          warehouseId: mov.warehouse_id || '',
          ocId: mov.oc_id ? Number(mov.oc_id) : null,
          ocCode: String(mov.oc_code || '').trim(),
          unitCost: (mov.unit_cost !== null && mov.unit_cost !== undefined && Number(mov.unit_cost) > 0)
            ? Number(mov.unit_cost)
            : null,
          ocProvider: String(mov.oc_provider || '').trim(),
          ocProviderNit: String(mov.oc_provider_nit || '').trim(),
        };
      };

      const toBogotaTs = (ymd, endOfDay) => {
        const s = String(ymd || '').trim();
        if (!s) return null;
        const t = endOfDay ? 'T23:59:59.999' : 'T00:00:00.000';
        const d = new Date(`${s}${t}-05:00`);
        const ts = d.getTime();
        return Number.isFinite(ts) ? ts : null;
      };

      const startTs = toBogotaTs(startDate, false);
      const endTs = toBogotaTs(endDate, true);
      const inRange = (r) => {
        const ts = (r && typeof r.ts === 'number') ? r.ts : null;
        if (ts === null) return false;
        if (startTs !== null && ts < startTs) return false;
        if (endTs !== null && ts > endTs) return false;
        return true;
      };

      const mvLimit = 1000;
      const buildParams = (extra = {}) => {
        const p = { limit: mvLimit, ...extra };
        if (startDate) p.start_date = startDate;
        if (endDate) p.end_date = endDate;
        return p;
      };

      const fetchPaged = async (paramsBase) => {
        let offset = 0;
        let all = [];
        let lastFirstId = null;
        while (true) {
          const res = await api.movements.get({ ...paramsBase, offset });
          const page = Array.isArray(res?.data) ? res.data : [];
          if (!page.length) break;
          const firstId = page[0]?.id ?? null;
          if (offset > 0 && firstId && firstId === lastFirstId) break;
          lastFirstId = firstId;
          all = all.concat(page);
          if (page.length < mvLimit) break;
          offset += mvLimit;
          if (all.length >= 50000) break;
        }
        return all;
      };

      const rowsFilteredAttempt = await fetchPaged(buildParams({ order: 'desc' }));
      const mappedAttempt = rowsFilteredAttempt.map(toUi);
      const filteredAttempt = (startDate || endDate) ? mappedAttempt.filter(inRange) : mappedAttempt;
      if (!(startDate || endDate) || filteredAttempt.length > 0 || rowsFilteredAttempt.length === 0) {
        return filteredAttempt;
      }

      let offset = 0;
      let lastFirstId = null;
      let collected = [];
      while (true) {
        const res = await api.movements.get({ limit: mvLimit, offset, order: 'asc' });
        const page = Array.isArray(res?.data) ? res.data : [];
        if (!page.length) break;
        const firstId = page[0]?.id ?? null;
        if (offset > 0 && firstId && firstId === lastFirstId) break;
        lastFirstId = firstId;
        const mapped = page.map(toUi);
        collected = collected.concat(mapped.filter(inRange));
        const last = mapped[mapped.length - 1];
        const lastTs = (last && typeof last.ts === 'number') ? last.ts : null;
        if (endTs !== null && lastTs !== null && lastTs > endTs) break;
        if (page.length < mvLimit) break;
        offset += mvLimit;
        if (offset >= 50000) break;
      }
      return collected;
    } catch (e) {
      console.error('searchMovements error', e);
      return [];
    }
  };

  // People Operations
  const addPerson = async (person) => {
    const normalizedDni = String(person?.dni || person?.id || '').replace(/\D/g, '');
    if (!normalizedDni) {
      return { success: false, message: 'Documento inválido. Ingresa solo números.' };
    }
    if (!String(person?.name || '').trim()) {
      return { success: false, message: 'Nombre requerido.' };
    }
    try {
      // Convertir datos del formulario a FormData para subir foto/firma/adjuntos como archivos
      const apiData = personFormToAPI({ ...person, id: normalizedDni, dni: normalizedDni });
      // Compatibilidad: si role no viene, backend mapea position->role
      if (person.role) apiData.append('role', person.role);
      await api.people.upsert(apiData);
      try {
        const dniUpload = normalizedDni;
        const isDataUrl = (v) => (typeof v === 'string' && v.startsWith('data:'));
        if (isDataUrl(person?.photo)) {
          await api.hr.files.upload({ category: 'photo', dni: dniUpload, photo: person.photo });
        }
        const attArr = Array.isArray(person?.attachments) ? person.attachments : [];
        const attFiles = attArr
          .map(a => (a && typeof a === 'object' && a.file instanceof File ? a.file : a))
          .filter(v => v instanceof File);
        const attDataUrls = attArr
          .map(a => (a && typeof a === 'object' && typeof a.data === 'string' ? a.data : a))
          .filter(v => isDataUrl(v));
        if (attFiles.length > 0 || attDataUrls.length > 0) {
          const fd = new FormData();
          fd.append('category', 'adjuntos');
          fd.append('dni', dniUpload);
          attFiles.forEach((f) => fd.append('files[]', f, f.name));
          attDataUrls.forEach((s) => fd.append('attachments[]', s));
          await api.hr.files.upload(fd);
          try {
            const listed = await api.hr.files.list({ category: 'adjuntos', dni: dniUpload });
            const rows = Array.isArray(listed?.data) ? listed.data : [];
            const baseOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
            const normalized = rows.map((r) => {
              const name = String(r?.name || '').trim();
              const urlRaw = String(r?.url || '').trim();
              if (!name || !urlRaw) return null;
              let urlAbs = urlRaw;
              try {
                if (baseOrigin) urlAbs = new URL(urlRaw, baseOrigin).toString();
              } catch (_) {}
              return { name, url: urlAbs };
            }).filter(Boolean);
            if (normalized.length) {
              const fd2 = new FormData();
              fd2.append('dni', normalizedDni);
              fd2.append('documento_identidad', normalizedDni);
              fd2.append('attachments', JSON.stringify(normalized));
              await api.people.upsert(fd2);
            }
          } catch (_) {}
        }
        if (isDataUrl(person?.signature)) {
          await api.hr.files.upload({ category: 'firma', dni: dniUpload, signature: person.signature });
        }
      } catch (_) {}
      // Intentar recuperar la persona para obtener URLs absolutas de foto/firma post-subida
      try {
        const got = await api.people.get({ dni: normalizedDni, clean: 1, limit: 1 });
        const arr = Array.isArray(got?.data) ? got.data : (got?.data ? [got.data] : []);
        const fresh = arr[0] || null;
        if (fresh) {
          let next;
          if (people.some((p) => String(p.dni || p.id) === normalizedDni)) {
            next = people.map(p => (String(p.dni || p.id) === normalizedDni ? {
              ...p,
              name: fresh.name || p.name,
              position: fresh.position || fresh.role || p.position,
              area: fresh.area || p.area,
              phone: fresh.phone || p.phone,
              email: fresh.email || p.email,
              status: fresh.status || p.status,
              photo: fresh.photo || fresh.photo_url || p.photo,
              signature: fresh.signature || fresh.signature_url || p.signature,
              attachments: Array.isArray(fresh.attachments) ? fresh.attachments : (p.attachments || []),
              documents: (fresh.documents && typeof fresh.documents === 'object') ? fresh.documents : (p.documents || {}),
            } : p));
          } else {
            next = [...people, {
              ...person,
              id: person.id || normalizedDni,
              dni: normalizedDni,
              _dbId: fresh._dbId ?? person._dbId ?? null,
              photo: fresh.photo || fresh.photo_url || person.photo,
              signature: fresh.signature || fresh.signature_url || person.signature,
              attachments: Array.isArray(fresh.attachments) ? fresh.attachments : (person.attachments || []),
              documents: (fresh.documents && typeof fresh.documents === 'object') ? fresh.documents : (person.documents || {}),
            }];
          }
          persistPeople(next);
          return { success: true };
        }
      } catch (_) {}
    } catch (e) { console.error('addPerson error', e); }
    const exists = people.some((p) => String(p.dni || p.id) === normalizedDni);
    const nextPerson = { ...person, id: person.id || Date.now(), dni: normalizedDni };
    const next = exists ? people.map(p => (String(p.dni || p.id) === normalizedDni ? nextPerson : p)) : [...people, nextPerson];
    persistPeople(next);
    try {
      const sigRaw = localStorage.getItem('senerfi_people_signatures');
      const sigMap = sigRaw ? JSON.parse(sigRaw) : {};
      if (nextPerson.signature) {
        sigMap[String(normalizedDni)] = nextPerson.signature;
        localStorage.setItem('senerfi_people_signatures', JSON.stringify(sigMap));
      }
    } catch (e) {}
    return { success: true };
  };

  const updatePerson = async (id, updatedPerson) => {
    const current = people.find(p => String(p.id) === String(id));
    const goingInactive = String(updatedPerson.status || '').toLowerCase() === 'inactivo';
    const hasActiveAssignments = assignments.some(a => String(a.responsibleDoc) === String(id) && String(a.status) === 'Asignado');

    if (goingInactive && hasActiveAssignments) {
      return { success: false, message: 'No se puede inactivar: la persona aún tiene ítems asignados en almacén.' };
    }

    let lastError = null;
    try {
      // Convertir datos a FormData para subir archivos correctamente
      const base = { ...(current || {}), ...(updatedPerson || {}) };
      const apiData = personFormToAPI(base);
      const dniVal = String(updatedPerson?.dni || current?.dni || id || '').replace(/\D/g, '');
      if (current?._dbId) apiData.append('id', String(current._dbId));
      if (dniVal) apiData.append('dni', dniVal);
      if (dniVal && !apiData.has('documento_identidad')) apiData.append('documento_identidad', dniVal);
      if (updatedPerson.role) apiData.append('role', updatedPerson.role);
      await api.people.upsert(apiData);
      try {
        const dniUpload = String(dniVal || id);
        const isDataUrl = (v) => (typeof v === 'string' && v.startsWith('data:'));
        if (isDataUrl(updatedPerson?.photo)) {
          await api.hr.files.upload({ category: 'photo', dni: dniUpload, photo: updatedPerson.photo });
        }
        const attArr = Array.isArray(updatedPerson?.attachments) ? updatedPerson.attachments : [];
        const attFiles = attArr
          .map(a => (a && typeof a === 'object' && a.file instanceof File ? a.file : a))
          .filter(v => v instanceof File);
        const attDataUrls = attArr
          .map(a => (a && typeof a === 'object' && typeof a.data === 'string' ? a.data : a))
          .filter(v => isDataUrl(v));
        if (attFiles.length > 0 || attDataUrls.length > 0) {
          const fd = new FormData();
          fd.append('category', 'adjuntos');
          fd.append('dni', dniUpload);
          attFiles.forEach((f) => fd.append('files[]', f, f.name));
          attDataUrls.forEach((s) => fd.append('attachments[]', s));
          await api.hr.files.upload(fd);
          try {
            const listed = await api.hr.files.list({ category: 'adjuntos', dni: dniUpload });
            const rows = Array.isArray(listed?.data) ? listed.data : [];
            const baseOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
            const normalized = rows.map((r) => {
              const name = String(r?.name || '').trim();
              const urlRaw = String(r?.url || '').trim();
              if (!name || !urlRaw) return null;
              let urlAbs = urlRaw;
              try {
                if (baseOrigin) urlAbs = new URL(urlRaw, baseOrigin).toString();
              } catch (_) {}
              return { name, url: urlAbs };
            }).filter(Boolean);
            if (normalized.length) {
              const fd2 = new FormData();
              if (current?._dbId) fd2.append('id', String(current._dbId));
              if (dniVal) fd2.append('dni', dniVal);
              if (dniVal) fd2.append('documento_identidad', dniVal);
              fd2.append('attachments', JSON.stringify(normalized));
              await api.people.upsert(fd2);
            }
          } catch (_) {}
        }
        if (isDataUrl(updatedPerson?.signature)) {
          await api.hr.files.upload({ category: 'firma', dni: dniUpload, signature: updatedPerson.signature });
        }
      } catch (e) { throw e; }
      // Refrescar datos desde backend para obtener rutas absolutas
      try {
        const got = await api.people.get({ dni: dniVal, clean: 1, limit: 1 });
        const arr = Array.isArray(got?.data) ? got.data : (got?.data ? [got.data] : []);
        const fresh = arr[0] || null;
        if (fresh) {
          const next = people.map(p => {
            if (p.id !== id) return p;
            const merged = { ...p, ...(fresh || {}) };
            merged.id = p.id;
            merged._dbId = p._dbId ?? fresh._dbId ?? null;
            if (!merged.photo) merged.photo = p.photo;
            if (!merged.signature) merged.signature = p.signature;
            return merged;
          });
          persistPeople(next);
          return { success: true };
        }
      } catch (_) {}
    } catch (e) { lastError = e; console.error('updatePerson error', e); }
    const msg = (lastError && lastError.message) ? String(lastError.message) : 'No fue posible guardar los cambios en el servidor';
    return { success: false, message: msg.replace(/^HTTP \d+:\s*/i, '') };
  };

  const deletePerson = async (id, opts = {}) => {
    const hasActiveAssignments = assignments.some(a => String(a.responsibleDoc) === String(id) && String(a.status) === 'Asignado');
    if (hasActiveAssignments) {
      return { success: false, message: 'No se puede inactivar: la persona aún tiene ítems asignados en almacén.' };
    }
    const current = people.find(p => String(p.id) === String(id));
    const endDate = String(opts?.contractEndDate || '').trim()
      || current?.contractEndDate
      || new Date().toISOString().split('T')[0];
    try {
      const res = await api.people.upsert({
        dni: String(id),
        name: current?.name || '',
        status: 'Inactivo',
        // El backend exige esta fecha al marcar Inactivo (people.php); sin
        // ella la desactivación fallaba en silencio y la fila igual
        // desaparecía de la lista local, aunque en la base seguía Activo.
        contractEndDate: endDate,
      });
      if (!res?.ok) {
        return { success: false, message: res?.error || 'No fue posible inactivar al colaborador.' };
      }
    } catch (e) {
      console.error('deletePerson error', e);
      const msg = e?.message ? String(e.message).replace(/^HTTP \d+:\s*/i, '') : 'No fue posible inactivar al colaborador.';
      return { success: false, message: msg };
    }
    const next = people.filter(p => String(p.id) !== String(id));
    persistPeople(next);
    return { success: true };
  };

  // Entry Operations
  const registerEntry = async (entryData) => {
    const material = materials.find(m => m.id === parseInt(entryData.materialId));
    if (!material) return { success: false, message: 'Material no encontrado' };

    // Serie opcional en entradas: solo validar duplicados si se proporciona
    const serialNorm = (entryData.serial || '').trim().toLowerCase();
    if (serialNorm) {
      const existsSerial = movements.some(m => (
        parseInt(m.materialId) === material.id && ((m.serial || '').trim().toLowerCase() === serialNorm)
      ));
      if (existsSerial) {
        return { success: false, message: 'Serial ya registrado para este material.' };
      }
    }

    const warehouseId = entryData.warehouseId || 'popayan';

    // Crear/actualizar brand_item solo si NO se envían serials
    // Ahora los serials se envían al backend en el movimiento y éste inserta brand_items.
    let brandItemId = null;
    try {
      const serialTrim = (entryData.serial || '').trim();
      if (!serialTrim && (entryData.brandName || '').trim()) {
        const bi = await api.brandItems.upsert({
          material_code: material.code,
          material_type: material.category || material.type || null,
          brand: (entryData.brandName || '').trim() || null,
          serial: null,
          status: 'Activo'
        });
        brandItemId = bi?.id || null;
      }
    } catch (e) { console.error('upsert brand_item in entry failed', e); }

      try {
        const serialTrim = (entryData.serial || '').trim();
        const payload = {
          type: 'entrada',
          material_id: material.id,
          warehouse_id: warehouseId,
          brand_item_id: brandItemId,
          brand: (entryData.brandName || '').trim() || null,
          requester_name: entryData.supplier || null,
          quantity: parseInt(entryData.quantity),
          notes: entryData.type + ' - ' + (entryData.observations || '')
        };
        if (currentUser?.id) payload.performed_by_user_id = currentUser.id;
        if (currentUser?.email) payload.performed_by_user_email = currentUser.email;
        if (currentUser?.name) payload.performed_by_user_name = currentUser.name;
        if (entryData.entryRef) {
          payload.entry_ref = String(entryData.entryRef);
        }
        if (entryData.ocId) payload.oc_id = entryData.ocId;
        if (entryData.ocCode) payload.oc_code = String(entryData.ocCode);
      // Enviar serial (singular) para compatibilidad con FormData en PHP
      // Nota: enviar arrays via FormData como 'serials' no llega como array en PHP.
      if (serialTrim) {
        payload.serial = serialTrim;
      }
      const respCreate = await api.movements.create(payload);
      if (!respCreate?.ok) {
        throw new Error(respCreate?.error || 'No fue posible registrar la entrada en backend');
      }
      try {
        const photos = Array.isArray(entryData.evidencePhotos) ? entryData.evidencePhotos.map(p => p?.dataUrl || p).filter(Boolean) : [];
        if (photos.length > 0) {
          await api.evidence.upload({ type: 'entrada', warehouse_id: warehouseId, entry_ref: String(entryData.entryRef || ''), photos });
        }
      } catch (_) {}
    } catch (e) {
      const msg = e?.message ? String(e.message) : 'No fue posible registrar la entrada en backend';
      if (/offline/i.test(msg)) {
        const newMovement = {
          id: Date.now(),
          type: 'Entrada',
          materialId: material.id,
          materialName: material.name,
          responsibleDoc: entryData.supplier,
          quantity: parseInt(entryData.quantity),
          serial: (entryData.serial || '').trim(),
          brandName: (entryData.brandName || '').trim(),
          date: formatBogotaYmdHms(new Date()),
          ts: Date.now(),
          user: 'admin',
          observations: entryData.type + ' - ' + entryData.observations,
          status: 'Completado',
          warehouseId,
          evidencePhotos: Array.isArray(entryData.evidencePhotos) ? entryData.evidencePhotos : []
        };
        persistMovements([...movements, newMovement]);
        const nextMaterials = materials.map(m => {
          if (m.id !== material.id) return m;
          const sbw = { ...(m.stockByWarehouse || {}) };
          const qty = parseInt(entryData.quantity);
          sbw[warehouseId] = (sbw[warehouseId] || 0) + qty;
          const newStockActual = Object.values(sbw).reduce((acc, v) => acc + (parseInt(v) || 0), 0);
          const nextLocation = (entryData.location || '').trim() || m.location || '';
          return { ...m, stockByWarehouse: sbw, stockActual: newStockActual, location: nextLocation };
        });
        persistMaterials(nextMaterials);
        bumpMovementsRefresh();
        return { success: true, message: 'Entrada guardada sin conexión' };
      }
      return { success: false, message: msg };
    }

    const newMovement = {
      id: Date.now(),
      type: 'Entrada',
      materialId: material.id,
      materialName: material.name,
      responsibleDoc: entryData.supplier,
      quantity: parseInt(entryData.quantity),
      serial: (entryData.serial || '').trim(),
      brandName: (entryData.brandName || '').trim(),
      date: formatBogotaYmdHms(new Date()),
      ts: Date.now(),
      user: 'admin',
      observations: entryData.type + ' - ' + entryData.observations,
      status: 'Completado',
      warehouseId,
      evidencePhotos: Array.isArray(entryData.evidencePhotos) ? entryData.evidencePhotos : []
    };
    persistMovements([...movements, newMovement]);

    const nextMaterials = materials.map(m => {
      if (m.id !== material.id) return m;
      const sbw = { ...(m.stockByWarehouse || {}) };
      const qty = parseInt(entryData.quantity);
      sbw[warehouseId] = (sbw[warehouseId] || 0) + qty;
      const newStockActual = Object.values(sbw).reduce((acc, v) => acc + (parseInt(v) || 0), 0);
      const nextLocation = (entryData.location || '').trim() || m.location || '';
      return { ...m, stockByWarehouse: sbw, stockActual: newStockActual, location: nextLocation };
    });
    persistMaterials(nextMaterials);
    bumpMovementsRefresh();
    return { success: true, message: 'Entrada registrada exitosamente' };
  };

  const registerBulkEntry = async (bulk) => {
    const materialId = parseInt(bulk?.materialId);
    const material = materials.find(m => m.id === materialId);
    if (!material) return { success: false, message: 'Material no encontrado' };
    const warehouseId = (bulk?.warehouseId || '').trim() || 'popayan';
    const entryRef = String(bulk?.entryRef || '').trim();
    const detailType = String(bulk?.detailType || bulk?.type || '').trim() || 'Compras';
    const supplier = String(bulk?.supplier || '').trim();
    const observations = String(bulk?.observations || '').trim();
    const location = String(bulk?.location || '').trim();
    const groups = Array.isArray(bulk?.groups) ? bulk.groups : [];
    const requiresSerial = Boolean(bulk?.requiresSerial) || !!material.requiresSerial || String(material?.hasSerial || '').toLowerCase() === 'sí';

    if (!groups.length) return { success: false, message: 'No hay ítems para registrar' };

    let ok = 0;
    let failed = 0;
    const nowTs = Date.now();
    const newMovs = [];
    let totalQty = 0;
    const serialErrors = [];

    for (let i = 0; i < groups.length; i += 1) {
      const g = groups[i] || {};
      const brand = String(g.brand || g.brandName || '').trim();
      const model = String(g.model || '').trim();
      const serials = Array.isArray(g.serials) ? g.serials.map(s => String(s || '').trim()).filter(Boolean) : [];
      const qty = requiresSerial ? serials.length : (parseInt(g.quantity) || 0);
      if (!qty || qty <= 0) { failed += 1; continue; }

      const payload = {
        type: 'entrada',
        material_id: material.id,
        warehouse_id: warehouseId,
        brand_item_id: null,
        brand: brand || null,
        model: model || null,
        material_type: material.category || material.type || null,
        requester_name: supplier || null,
        quantity: qty,
        notes: detailType + ' - ' + observations,
      };
      if (entryRef) payload.entry_ref = entryRef;
      if (bulk?.ocId) payload.oc_id = bulk.ocId;
      if (bulk?.ocCode) payload.oc_code = String(bulk.ocCode);
      if (requiresSerial && serials.length > 0) payload.serials = serials;
      if (currentUser?.id) payload.performed_by_user_id = currentUser.id;
      if (currentUser?.email) payload.performed_by_user_email = currentUser.email;
      if (currentUser?.name) payload.performed_by_user_name = currentUser.name;

      try {
        const resp = await api.movements.create(payload);
        if (resp && Array.isArray(resp.serials_errors) && resp.serials_errors.length) {
          resp.serials_errors.forEach((msg) => serialErrors.push(`${material.code || material.name}: ${msg}`));
        }
        if (resp && resp.ok) {
          ok += 1;
          totalQty += qty;
          newMovs.push({
            id: nowTs + i,
            type: 'Entrada',
            materialId: material.id,
            materialName: material.name,
            responsibleDoc: supplier || null,
            quantity: qty,
            serial: '',
            brandName: brand,
            date: formatBogotaYmdHms(new Date()),
            ts: nowTs + i,
            user: 'admin',
            observations: detailType + ' - ' + observations,
            status: 'Completado',
            warehouseId,
            evidencePhotos: []
          });
        } else {
          failed += 1;
        }
      } catch (e) {
        failed += 1;
      }
    }

    if (ok > 0) {
      persistMovements([...movements, ...newMovs]);
      const nextMaterials = materials.map(m => {
        if (m.id !== material.id) return m;
        const sbw = { ...(m.stockByWarehouse || {}) };
        sbw[warehouseId] = (sbw[warehouseId] || 0) + totalQty;
        const newStockActual = Object.values(sbw).reduce((acc, v) => acc + (parseInt(v) || 0), 0);
        const nextLocation = location || m.location || '';
        return { ...m, stockByWarehouse: sbw, stockActual: newStockActual, location: nextLocation };
      });
      persistMaterials(nextMaterials);
      bumpMovementsRefresh();
    }

    return { success: failed === 0 && serialErrors.length === 0, ok, failed, totalQty, serialErrors };
  };

  // Assignment Operations
  const registerAssignment = async (assignmentData) => {
    const mvType = assignmentData.type || 'Asignación';
    const mvTypeDb = mvType === 'Préstamo' ? 'prestamo' : 'asignacion';
    const material = materials.find(m => m.id === parseInt(assignmentData.materialId));
    const vehicleId = Number(assignmentData.vehicleId || assignmentData.vehicle_id || 0);
    const assigneeType = assignmentData.assigneeType === 'vehicle' || vehicleId > 0 ? 'vehicle' : 'person';
    const isVehicleAssignee = assigneeType === 'vehicle' && vehicleId > 0;
    const person = !isVehicleAssignee
      ? people.find(p => String(p.dni || p.id) === String(assignmentData.responsibleDoc))
      : null;

    if (!material) {
      const hasPerson = !!person;
      const warehouseId = (assignmentData.warehouseId || '').trim() || null;
    const newMovement = {
      id: Date.now(),
      type: mvType,
        materialId: null,
        materialName: 'Sin material',
        serial: '',
        brandName: '',
        responsibleDoc: hasPerson ? (person.dni || null) : null,
        responsibleName: hasPerson ? person.name : 'Sin responsable',
        quantity: parseInt(assignmentData.quantity) || 0,
        date: formatBogotaYmdHms(new Date()),
        user: 'admin',
        observations: assignmentData.observations,
        evidencePhotos: Array.isArray(assignmentData.evidencePhotos) ? assignmentData.evidencePhotos : [],
        status: 'Completado',
        warehouseId,
      };

      setMovements([...movements, newMovement]);
      // No se generan cambios de stock ni registro de asignación sin material
      return { success: true, message: 'Movimiento guardado exitosamente' };
    }

    if (isVehicleAssignee && !vehicleId) {
      return { success: false, message: 'Seleccione un vehículo (catálogo Transportes).' };
    }
    if (!isVehicleAssignee && !person) {
      return { success: false, message: 'Material o persona no encontrados' };
    }

    const vehiclePlate = String(assignmentData.vehiclePlate || assignmentData.responsibleName || '').replace(/^Vehículo\s+/i, '').trim();
    const vehicleLabel = vehiclePlate ? `Vehículo ${vehiclePlate}` : `Vehículo #${vehicleId}`;

    const warehouseId = normalizeWarehouseId(assignmentData.warehouseId || 'popayan');
    // No bloquear por stock local desactualizado; permitir que backend valide y responda
    // Mantener chequeo suave para UX: si local indica 0, avisar pero intentar
    const reqQty = parseInt(assignmentData.quantity);
    if (!Number.isFinite(reqQty) || reqQty <= 0) {
      return { success: false, message: 'Cantidad inválida para asignación' };
    }

    const serialTrim = (assignmentData.serial || '').trim();
    const isSyntheticSerial = /^NS-\d+-\d+$/.test(serialTrim);
    if (isSyntheticSerial) {
      return { success: false, message: 'El serial seleccionado no es válido.' };
    }

    let requiresSerial = Boolean(material?.requiresSerial)
      || (material?.requires_serial === 1)
      || (String(material?.requires_serial || '').toLowerCase() === 'true')
      || !!serialTrim;
    if (!requiresSerial && !serialTrim) {
      try {
        const res = await api.serials.list({ material_id: material.id, warehouse_id: warehouseId, limit: 5 });
        const arr = Array.isArray(res?.data) ? res.data : (res?.data ? [res.data] : []);
        const hasReal = arr.some((r) => {
          const s = String(r?.serial || '').trim();
          return !!s && !/^NS-\d+-\d+$/.test(s);
        });
        if (hasReal) requiresSerial = true;
      } catch (_) {}
    }
    if (requiresSerial && !serialTrim) {
      return { success: false, message: 'Este material requiere serial. Seleccione un serial disponible.' };
    }
    if (requiresSerial && reqQty !== 1) {
      return { success: false, message: 'Para ítems seriados, la asignación debe ser de 1 unidad por operación.' };
    }

    let brandItemId = null;
    let brandResolved = (assignmentData.brandName || '').trim();
    if (serialTrim) {
      let found = false;
      let isAssigned = false;
      try {
        const res = await api.serials.list({ material_id: material.id, warehouse_id: warehouseId, serial: serialTrim, include_all: 1, include_deleted: 1, limit: 5 });
        const arr = Array.isArray(res?.data) ? res.data : (res?.data ? [res.data] : []);
        const row = arr.find((r) => String(r?.serial || '').trim() === serialTrim) || null;
        if (row) {
          found = true;
          const owner = String(row?.owner_dni || '').trim();
          const lastType = String(row?.last_type || '').trim().toLowerCase();
          if (owner) isAssigned = true;
          if (lastType === 'asignacion' || lastType === 'prestamo' || lastType === 'préstamo') isAssigned = true;
        }
      } catch (_) {}

      if (!found) {
        const entrySerialsLocal = (movements || [])
          .filter(mv => mv.type === 'Entrada' && mv.materialId === material.id && (mv.serial || '').trim() && mv.warehouseId === warehouseId)
          .map(mv => (mv.serial || '').trim());
        found = entrySerialsLocal.includes(serialTrim);
      }
      const assignedSerialsLocal = (assignments || [])
        .filter(a => a.materialId === material.id && a.status === 'Asignado' && (a.serial || '').trim() && a.warehouseId === warehouseId)
        .map(a => (a.serial || '').trim());
      isAssigned = isAssigned || assignedSerialsLocal.includes(serialTrim);

      if (!found) {
        return { success: false, message: 'El serial seleccionado no existe en la bodega para este material.' };
      }
      if (isAssigned) {
        return { success: false, message: 'El serial seleccionado ya está asignado actualmente.' };
      }

      try {
        const biRes = await api.brandItems.get({ material_code: material.code, serial: serialTrim, warehouse_id: warehouseId, include_deleted: 1, limit: 5, order: 'desc' });
        const biList = Array.isArray(biRes?.data) ? biRes.data : (biRes?.data ? [biRes.data] : []);
        const bi = biList.find((x) => String(x?.serial || '').trim() === serialTrim) || biList[0] || null;
        brandItemId = bi?.id || null;
        if (!brandResolved) brandResolved = String(bi?.brand || bi?.name || '').trim();
      } catch (e) {
        console.error('brand_items.get for asignacion failed', e);
      }

      if (!brandItemId) {
        try {
          const bi = await api.brandItems.upsert({
            material_code: material.code,
            material_type: material.category || material.type || null,
            brand: brandResolved || null,
            serial: serialTrim,
            status: 'Activo',
            warehouse_id: warehouseId
          });
          brandItemId = bi?.id || null;
        } catch (e) {
          console.error('brand_item upsert for asignacion failed', e);
        }
      }
    } else {
      if (brandResolved) {
        try {
          const bi = await api.brandItems.upsert({
            material_code: material.code,
            material_type: material.category || material.type || null,
            brand: brandResolved || null,
            serial: null,
            status: 'Activo',
            warehouse_id: warehouseId
          });
          brandItemId = bi?.id || null;
        } catch (e) { console.error('upsert brand_item in asignacion failed', e); }
      }
    }

    let createOk = false;
    let createErr = null;
    let movementId = null;
    let serverStockAfter = null;
    let warehouseFromServer = warehouseId;
    const movementPayload = {
      type: mvTypeDb,
      material_id: material.id,
      warehouse_id: warehouseId,
      brand_item_id: brandItemId,
      serial: serialTrim || undefined,
      brand: brandResolved || undefined,
      quantity: reqQty,
      notes: assignmentData.observations || '',
      performed_by_user_id: currentUser?.id || null,
      performed_by_user_email: currentUser?.email || null,
      performed_by_user_name: currentUser?.name || null,
    };
    if (isVehicleAssignee) {
      movementPayload.vehicle_id = vehicleId;
      movementPayload.assignee_type = 'vehicle';
      movementPayload.requester_name = vehiclePlate || vehicleLabel;
    } else {
      movementPayload.dni = String(person.dni || assignmentData.responsibleDoc || '');
      movementPayload.requester_name = person.name || '';
      movementPayload.assignee_type = 'person';
    }
    const coGroup = String(assignmentData.coGroup || assignmentData.co_group || '').trim();
    if (coGroup) {
      movementPayload.co_group = coGroup;
    }

    try {
      const resp = await api.movements.create(movementPayload);
      if (resp && resp.ok) {
        createOk = true;
        movementId = resp.movement_id || resp.id || null;
        if (typeof resp.stock === 'number') serverStockAfter = resp.stock;
        if (resp.warehouse_id) warehouseFromServer = normalizeWarehouseId(resp.warehouse_id);
      }
    } catch (e) {
      createErr = e;
      console.error('movements.create asignacion failed', e);
    }

    // Persistir solo serial en API de serials (Asignación no gestiona vencimiento)
    try {
      if (serialTrim && !isVehicleAssignee) {
        await api.serials.upsert({
          material_code: material.code,
          serial: serialTrim,
          warehouse_id: warehouseId,
          status: 'Asignado',
          owner_dni: String(person.dni || assignmentData.responsibleDoc || ''),
        });
      }
    } catch (e) {
      console.warn('serials.upsert metadata failed', e);
    }
    try {
      const photos = Array.isArray(assignmentData.evidencePhotos) ? assignmentData.evidencePhotos.map(p => p?.dataUrl || p).filter(Boolean) : [];
      if (photos.length > 0 && movementId && !isVehicleAssignee) {
        await api.evidence.upload({ type: mvTypeDb, warehouse_id: warehouseId, movement_id: movementId, dni: String(person.dni || ''), photos });
      }
    } catch (_) {}
    if (!createOk) {
      const msg = createErr?.message || 'No fue posible crear el movimiento en backend';
      return { success: false, message: msg };
    }

    // Intentar recuperar id de asignación recién creada (aproximación: última por persona/material)
    let backendAssignId = null;
    try {
      const assignFilters = isVehicleAssignee
        ? { vehicle_id: vehicleId, material_id: material.id, limit: 1, order: 'desc', active_only: 1 }
        : { dni: person.dni || assignmentData.responsibleDoc, material_id: material.id, limit: 1, order: 'desc' };
      const lastA = await api.assignments.get(assignFilters);
      const arr = Array.isArray(lastA?.data) ? lastA.data : (lastA?.data ? [lastA.data] : []);
      backendAssignId = arr[0]?.id || null;
    } catch (e) { console.error('fetch last assignment failed', e); }

    const newAssignment = {
      id: backendAssignId || Date.now(),
      assignmentDbId: backendAssignId || null,
      materialId: material.id,
      materialName: material.name,
      materialCode: material.code || '',
      materialCategory: material.category || material.type || '',
      serial: serialTrim,
      brandName: brandResolved,
      assigneeType,
      vehicleId: isVehicleAssignee ? vehicleId : null,
      vehiclePlate: isVehicleAssignee ? vehiclePlate : '',
      responsibleDoc: isVehicleAssignee ? `vehicle:${vehicleId}` : person.dni,
      responsibleName: isVehicleAssignee ? vehicleLabel : person.name,
      quantity: parseInt(assignmentData.quantity),
      assignDate: new Date().toISOString().split('T')[0],
      hasPeripherals: serialTrim ? 'Sí' : 'No',
      status: 'Asignado',
      type: mvType,
      observations: assignmentData.observations,
      evidencePhotos: Array.isArray(assignmentData.evidencePhotos) ? assignmentData.evidencePhotos : [],
      warehouseId
    };

    const newMovement = {
      id: Date.now() + 1,
      type: mvType,
      materialId: material.id,
      materialName: material.name,
      serial: serialTrim,
      brandName: brandResolved,
      responsibleDoc: isVehicleAssignee ? `vehicle:${vehicleId}` : person.dni,
      responsibleName: isVehicleAssignee ? vehicleLabel : person.name,
      quantity: parseInt(assignmentData.quantity),
      date: formatBogotaYmdHms(new Date()),
      ts: Date.now(),
      user: 'admin',
      observations: assignmentData.observations,
      evidencePhotos: Array.isArray(assignmentData.evidencePhotos) ? assignmentData.evidencePhotos : [],
      status: 'Completado',
      warehouseId,
    };

    persistAssignments([...assignments, newAssignment]);
    // Refrescar movimientos desde backend para reflejar el registro real
    try {
      const mvRes2 = await api.movements.get({ limit: 50, order: 'desc' });
      const mvRows2 = Array.isArray(mvRes2?.data) ? mvRes2.data : [];
      const typed2 = mvRows2.map(mov => {
        const bi = brandItems.find(b => Number(b.id) === Number(mov.brand_item_id));
        const t = String(mov.type || '').toLowerCase();
        const typeUi = t === 'entrada' ? 'Entrada' : t === 'asignacion' ? 'Asignación' : t === 'devolucion' ? 'Devolución' : t === 'consumible' ? 'Consumible' : t === 'prestamo' ? 'Préstamo' : 'Salida';
        const nameNorm = String(mov.requester_name || '').trim().toLowerCase();
        const personMatch = people.find(p => String(p.name || '').trim().toLowerCase() === nameNorm);
        const dniResolved = mov.requester_dni || mov.dni || personMatch?.dni || personMatch?.id || null;
        const unitResolved = mov.material_unit || (materials.find(mm => Number(mm.id) === Number(mov.material_id))?.unit) || 'Unidad';
        return {
          id: mov.id,
          type: typeUi,
          materialId: mov.material_id,
          materialName: mov.material_name || '',
          materialCode: mov.material_code || '',
          materialUnit: unitResolved,
          unit: unitResolved,
          responsibleName: mov.requester_name || '',
          responsibleDoc: dniResolved,
          quantity: mov.quantity,
          date: mov.created_at ? fmtDateTime(mov.created_at) : formatBogotaYmdHms(new Date()),
          ts: mov.created_at ? parseBogotaDate(mov.created_at).getTime() : Date.now(),
          user: mov.performed_by_name || mov.performed_by_email || '',
          observations: mov.notes || '',
          entryRef: mov.entry_ref || '',
          status: 'Completado',
          serial: bi?.serial || '',
          brandName: bi?.name || '',
          warehouseId: mov.warehouse_id || ''
        };
      });
      persistMovements(typed2);
    } catch (_) {
      persistMovements([...movements, newMovement]);
    }
    const nextMaterials2 = materials.map(m => {
      if (m.id !== material.id) return m;
      const sbw2 = { ...(m.stockByWarehouse || {}) };
      const whKey = normalizeWarehouseId(warehouseFromServer || warehouseId);
      if (serverStockAfter !== null) {
        sbw2[whKey] = serverStockAfter;
      } else {
        sbw2[whKey] = (sbw2[whKey] || 0) - reqQty;
      }
      const newStockActual = Object.values(sbw2).reduce((acc, v) => acc + (parseInt(v) || 0), 0);
      return { ...m, stockByWarehouse: sbw2, stockActual: newStockActual };
    });
    persistMaterials(nextMaterials2);
    return { success: true, message: 'Asignación registrada exitosamente', movementId };
  };

  // Return Operations
  const registerReturn = async (returnData) => {
    const assignmentIdRaw = String(returnData.assignmentId ?? '').trim();
    const assignmentLocal = assignments.find(a => String(a.id) === assignmentIdRaw);
    const assignment = assignmentLocal || {
      id: (assignmentIdRaw && assignmentIdRaw !== '0') ? assignmentIdRaw : Date.now(),
      materialId: returnData.materialId ? parseInt(returnData.materialId) : null,
      materialName: returnData.materialName || '',
      materialCode: '',
      materialCategory: '',
      serial: String(returnData.serial || '').trim(),
      brandName: String(returnData.brandName || '').trim(),
      responsibleDoc: returnData.responsibleDoc || null,
      responsibleName: returnData.responsibleName || '',
      quantity: parseInt(returnData.assignedQuantity) || 0,
      assignDate: '',
      hasPeripherals: String(returnData.serial || '').trim() ? 'Sí' : 'No',
      status: 'Asignado',
      type: 'Asignación',
      observations: '',
      evidencePhotos: [],
      warehouseId: returnData.warehouseId || ''
    };
    if (!assignment?.materialId) {
      return { success: false, message: 'Asignación no encontrada' };
    }

    const material = materials.find(m => String(m.id) === String(assignment.materialId));
    if (!material) {
      return { success: false, message: 'Material no encontrado para la asignación seleccionada' };
    }

    const returnQty = parseInt(returnData.returnQuantity) || 0;
    if (returnQty <= 0) {
      return { success: false, message: 'Ingrese una cantidad válida a devolver' };
    }
    if (returnQty > assignment.quantity) {
      return { success: false, message: 'La cantidad a devolver no puede ser mayor a la asignada' };
    }

    const warehouseId = assignment.warehouseId || 'popayan';

    // localizar brand_item por material_code + serial (si lo hay)
    let brandItemId = null;
    const serialEffective = String(returnData.serial || assignment.serial || '').trim();
    let brandResolved = String(returnData.brandName || assignment.brandName || '').trim();
    if (serialEffective) {
      try {
        const biRes = await api.brandItems.get({ material_code: material.code, serial: serialEffective, include_deleted: 1, limit: 5, order: 'desc' });
        const biList = Array.isArray(biRes?.data) ? biRes.data : (biRes?.data ? [biRes.data] : []);
        brandItemId = biList[0]?.id || null;
        if (!brandResolved) brandResolved = String(biList[0]?.brand || '').trim();
      } catch (e) { console.error('find brand_item for return failed', e); }
    }

    const custodyVehicleId = Number(
      returnData.custodyVehicleId
      || assignment.custodyVehicleId
      || 0
    );
    const custodyFromNotes = (() => {
      const raw = String(assignment.observations || returnData.observations || '');
      const m = raw.match(/\[custodia_vehiculo:(\d+)\]/);
      return m ? Number(m[1]) : 0;
    })();
    const returnVehicleId = custodyVehicleId > 0 ? custodyVehicleId : custodyFromNotes;
    const returnByVehicle = returnVehicleId > 0;

    let ok = false;
    try {
      const returnPayload = {
        type: 'devolucion',
        material_id: material.id,
        warehouse_id: warehouseId,
        brand_item_id: brandItemId,
        brand: brandResolved || null,
        serial: serialEffective || null,
        requester_name: returnData.responsibleName || assignment.responsibleName || String(assignment.responsibleDoc || ''),
        quantity: returnQty,
        notes: 'Estado: ' + returnData.materialState + ' - ' + (returnData.observations || ''),
        performed_by_user_id: currentUser?.id || null,
        performed_by_user_email: currentUser?.email || null,
        performed_by_user_name: currentUser?.name || null,
      };
      if (returnByVehicle) {
        returnPayload.vehicle_id = returnVehicleId;
        returnPayload.assignee_type = 'vehicle';
        const personDoc = String(returnData.responsibleDoc || assignment.responsibleDoc || '').trim();
        if (personDoc && !/^vehicle:\d+$/i.test(personDoc)) {
          returnPayload.dni = personDoc;
        }
        returnPayload.requester_name = returnData.responsibleName || assignment.responsibleName || personDoc || '';
      } else {
        returnPayload.dni = assignment.responsibleDoc || null;
      }
      const resp = await api.movements.create(returnPayload);
      if (resp && resp.ok) ok = true;
    } catch (e) { console.error('movements.create devolucion failed', e); }
    if (!ok) {
      return { success: false, message: 'No fue posible crear la devolución en backend' };
    }

    // Limpiar dueño y vencimiento del serial devuelto para que cese alertas
    try {
      const serialTrim = serialEffective;
      if (serialTrim) {
        await api.serials.upsert({
          material_code: material.code,
          warehouse_id: warehouseId,
          brand_item_id: brandItemId || null,
          serial: serialTrim,
          owner_dni: '',
          requires_expire: 0,
          warranty_until: ''
        });
      }
    } catch (e) {
      console.warn('serials.upsert clear on return failed', e);
    }

    const newMovement = {
      id: Date.now(),
      type: 'Devolución',
      materialId: material.id,
      materialName: material.name,
      responsibleDoc: assignment.responsibleDoc,
      responsibleName: returnData.responsibleName || assignment.responsibleName,
      quantity: returnQty,
      date: formatBogotaYmdHms(new Date()),
      ts: Date.now(),
      user: 'admin',
      observations: 'Estado: ' + returnData.materialState + ' - ' + returnData.observations,
      status: 'Completado',
      serial: serialEffective,
      brandName: brandResolved || String(assignment.brandName || '').trim(),
      warehouseId,
    };
    try {
      const mvRes2 = await api.movements.get({ limit: 50, order: 'desc' });
      const mvRows2 = Array.isArray(mvRes2?.data) ? mvRes2.data : [];
      const typed2 = mvRows2.map(mov => {
        const bi = brandItems.find(b => Number(b.id) === Number(mov.brand_item_id));
        const t = String(mov.type || '').toLowerCase();
        const typeUi = t === 'entrada' ? 'Entrada' : t === 'asignacion' ? 'Asignación' : t === 'devolucion' ? 'Devolución' : t === 'prestamo' ? 'Préstamo' : 'Salida';
        const nameNorm = String(mov.requester_name || '').trim().toLowerCase();
        const personMatch = people.find(p => String(p.name || '').trim().toLowerCase() === nameNorm);
        const dniResolved = mov.requester_dni || mov.dni || personMatch?.dni || personMatch?.id || null;
        const unitResolved = mov.material_unit || (materials.find(mm => Number(mm.id) === Number(mov.material_id))?.unit) || 'Unidad';
        return {
          id: mov.id,
          type: typeUi,
          materialId: mov.material_id,
          materialName: mov.material_name || '',
          materialCode: mov.material_code || '',
          materialUnit: unitResolved,
          unit: unitResolved,
          responsibleName: mov.requester_name || '',
          responsibleDoc: dniResolved,
          quantity: mov.quantity,
          date: mov.created_at ? fmtDateTime(mov.created_at) : formatBogotaYmdHms(new Date()),
          ts: mov.created_at ? parseBogotaDate(mov.created_at).getTime() : Date.now(),
          user: mov.performed_by_name || mov.performed_by_email || '',
          observations: mov.notes || '',
          entryRef: mov.entry_ref || '',
          status: 'Completado',
          serial: String(mov.serial || '').trim() || bi?.serial || '',
          brandName: String(mov.brand || '').trim() || bi?.name || '',
          warehouseId: mov.warehouse_id || ''
        };
      });
      persistMovements(typed2);
    } catch (_) {
      persistMovements([...movements, newMovement]);
    }

    // El backend ya decrementó (o eliminó) la(s) fila(s) de assignments
    // correspondientes dentro de la misma transacción que creó el
    // movimiento de devolución (server/api/movements.php, applyReturnToRow)
    // — incluyendo el caso de varias filas para el mismo
    // material+persona+bodega, que se consumen en orden FIFO. Volver a
    // escribir aquí con un valor calculado en el cliente era redundante y,
    // si había más de una fila en ese alcance, terminaba corrigiendo la
    // fila equivocada (y descontando dos veces). En vez de eso, se vuelve a
    // consultar el estado real de esas filas y se reemplaza en el estado
    // local.
    const scopeDni = String(assignment.responsibleDoc || '').trim();
    const isInScope = (a) => String(a.responsibleDoc || '').trim() === scopeDni
      && String(a.materialId) === String(material.id)
      && String(a.warehouseId || '') === String(warehouseId);
    try {
      const freshScope = await api.assignments.get({
        dni: scopeDni,
        material_id: material.id,
        warehouse_id: warehouseId,
        limit: 200,
        order: 'asc',
      });
      const freshRows = Array.isArray(freshScope?.data) ? freshScope.data : [];
      const freshUi = freshRows.map(a => ({
        id: a.id,
        assignmentDbId: a.id,
        materialId: a.material_id,
        materialName: a.material_name || material.name || '',
        materialCode: a.material_code || material.code || '',
        materialCategory: a.material_category || a.materialCategory || '',
        responsibleDoc: a.person_dni || a.person_id,
        responsibleName: a.person_name || String(a.person_dni || a.person_id || ''),
        quantity: Number(a.quantity) || 0,
        assignDate: a.created_at ? String(a.created_at).slice(0, 10) : new Date().toISOString().slice(0, 10),
        serial: String(a.serial || '').trim(),
        brandName: String(a.brand || '').trim(),
        hasPeripherals: String(a.serial || '').trim() ? 'Sí' : 'No',
        status: (Number(a.quantity) || 0) <= 0 ? 'Devuelto' : 'Asignado',
        observations: a.notes || '',
        evidencePhotos: [],
        warehouseId: a.warehouse_id || '',
      }));
      persistAssignments([...assignments.filter(a => !isInScope(a)), ...freshUi]);
    } catch (e) {
      console.error('No fue posible refrescar assignments tras la devolución', e);
      const nextAssignments = assignmentLocal
        ? assignments.map(a => a.id === assignment.id
          ? (returnQty === assignment.quantity
            ? { ...a, status: 'Devuelto', quantity: 0 }
            : { ...a, quantity: Math.max(0, a.quantity - returnQty) })
          : a)
        : assignments;
      persistAssignments(nextAssignments);
    }

    const nextMaterials3 = materials.map(m => {
      if (m.id !== material.id) return m;
      const sbw = { ...(m.stockByWarehouse || {}) };
      sbw[warehouseId] = (sbw[warehouseId] || 0) + returnQty;
      const newStockActual = Object.values(sbw).reduce((acc, v) => acc + (parseInt(v) || 0), 0);
      return { ...m, stockByWarehouse: sbw, stockActual: newStockActual };
    });
    persistMaterials(nextMaterials3);
    bumpMovementsRefresh();
    return { success: true, message: 'Devolución registrada exitosamente' };
  };

  // Exit Operations
  const registerExit = async (exitData) => {
    const material = materials.find(m => m.id === parseInt(exitData.materialId));
    if (!material) {
      return { success: false, message: 'Material no encontrado' };
    }

    const exitQty = parseInt(exitData.quantity);
    const warehouseId = exitData.warehouseId || 'popayan';
    if (!Number.isFinite(exitQty) || exitQty <= 0) {
      return { success: false, message: 'Cantidad inválida para salida' };
    }

    const serialTrim = (exitData.serial || '').trim();
    const isSerial = !!material.requiresSerial || !!serialTrim;
    if (isSerial && !serialTrim) {
      return { success: false, message: 'Este material requiere serie. Ingrese la serie.' };
    }
    if (isSerial && exitQty !== 1) {
      return { success: false, message: 'Para ítems seriados, la salida debe ser de 1 unidad por operación.' };
    }

    let brandItemId = null;
    let brandResolved = (exitData.brandName || '').trim();
    if (isSerial) {
      try {
        const biRes = await api.brandItems.get({ material_code: material.code, serial: serialTrim, warehouse_id: warehouseId, limit: 5, order: 'desc' });
        const biList = Array.isArray(biRes?.data) ? biRes.data : (biRes?.data ? [biRes.data] : []);
        const bi = biList.find((x) => String(x?.serial || '').trim() === serialTrim) || biList[0] || null;
        brandItemId = bi?.id || null;
        if (!brandResolved) brandResolved = String(bi?.brand || bi?.name || '').trim();
      } catch (e) {
        console.error('brand_items.get for exit failed', e);
      }
      if (!brandItemId) {
        return { success: false, message: 'No se encontró el serial en la bodega (brand_items).' };
      }
    }

    let okExit = false;
    let exitErr = null;
    let consecutivo = null;
    const reqName = exitData.responsibleName || 'Sistema';
    const reqDni = exitData.responsibleDoc || null;

    const isConsumible = String(exitData.exitType || '').toLowerCase() === 'consumible';
    const apiType = isConsumible ? 'consumible' : 'salida';

    try {
      const payload = {
        type: apiType,
        material_id: material.id,
        warehouse_id: warehouseId,
        brand_item_id: brandItemId,
        requester_name: reqName,
        dni: reqDni,
        quantity: exitQty,
        notes: (isConsumible ? 'Consumible' : exitData.reason) + ' - ' + (exitData.observations || ''),
        evidence_photos: Array.isArray(exitData.evidencePhotos) ? exitData.evidencePhotos : [],
        performed_by_user_id: currentUser?.id || null,
        performed_by_user_email: currentUser?.email || null,
        performed_by_user_name: currentUser?.name || null
      };
      // Consumos desde una sub-bodega: número de obra/caso/ticket/consigna
      // y municipio, validados/obligatorios en el backend (ver movements.php)
      if (exitData.referenceNumber) payload.reference_number = exitData.referenceNumber;
      if (exitData.municipio) payload.municipio = exitData.municipio;
      // Fase 3 del puente Almacén–Compras: centro de costo del consumo.
      if (exitData.costCenter) payload.cost_center = exitData.costCenter;
      const resp = await api.movements.create(payload);
      if (resp && resp.ok) {
        okExit = true;
        consecutivo = resp.consecutivo || null;
      }
    } catch (e) {
      exitErr = e;
      console.error('movements.create salida failed', e);
    }
    if (!okExit) {
      return { success: false, message: exitErr?.message || 'No fue posible crear la salida en backend' };
    }

    const newMovement = {
      id: Date.now(),
      type: (String(exitData.exitType || '').toLowerCase() === 'consumible') ? 'Consumible' : (String(exitData.exitType || '').toLowerCase() === 'prestamo' || String(exitData.exitType || '').toLowerCase() === 'préstamo') ? 'Préstamo' : ('Salida - ' + exitData.exitType),
      materialId: material.id,
      materialName: material.name,
      responsibleDoc: reqDni || 'Sistema',
      responsibleName: reqName,
      quantity: exitQty,
      date: formatBogotaYmdHms(new Date()),
      ts: Date.now(),
      user: 'admin',
      observations: exitData.reason + ' - ' + (exitData.observations || ''),
      status: 'Completado',
      serial: (exitData.serial || '').trim(),
      brandName: brandResolved,
      warehouseId,
      warehouseName: (warehouses.find(w => String(w.id) === String(warehouseId))?.name || warehouseId)
    };
    try {
      const mvRes2 = await api.movements.get({ limit: 50, order: 'desc' });
      const mvRows2 = Array.isArray(mvRes2?.data) ? mvRes2.data : [];
      const typed2 = mvRows2.map(mov => {
        const bi = brandItems.find(b => Number(b.id) === Number(mov.brand_item_id));
        let t = String(mov.type || '').toLowerCase();
        if (t === 'salida' && String(mov.notes || '').toLowerCase().startsWith('consumible')) {
          t = 'consumible';
        }
        const typeUi = t === 'entrada' ? 'Entrada' : t === 'asignacion' ? 'Asignación' : t === 'devolucion' ? 'Devolución' : t === 'prestamo' ? 'Préstamo' : t === 'consumible' ? 'Consumible' : 'Salida';
        const nameNorm = String(mov.requester_name || '').trim().toLowerCase();
        const personMatch = people.find(p => String(p.name || '').trim().toLowerCase() === nameNorm);
        const dniResolved = mov.requester_dni || mov.dni || personMatch?.dni || personMatch?.id || null;
        const unitResolved = mov.material_unit || (materials.find(mm => Number(mm.id) === Number(mov.material_id))?.unit) || 'Unidad';
        return {
          id: mov.id,
          type: typeUi,
          materialId: mov.material_id,
          materialName: mov.material_name || '',
          materialCode: mov.material_code || '',
          materialUnit: unitResolved,
          unit: unitResolved,
          responsibleName: mov.requester_name || '',
          responsibleDoc: dniResolved,
          quantity: mov.quantity,
          date: mov.created_at ? fmtDateTime(mov.created_at) : formatBogotaYmdHms(new Date()),
          ts: mov.created_at ? parseBogotaDate(mov.created_at).getTime() : Date.now(),
          user: mov.performed_by_name || mov.performed_by_email || '',
          observations: mov.notes || '',
          entryRef: mov.entry_ref || '',
          status: 'Completado',
          serial: bi?.serial || '',
          brandName: bi?.name || '',
          warehouseId: mov.warehouse_id || ''
        };
      });
      persistMovements(typed2);
    } catch (_) {
      persistMovements([...movements, newMovement]);
    }

    const nextMaterials4 = materials.map(m => {
      if (m.id !== material.id) return m;
      const nextSBW = { ...(m.stockByWarehouse || {}) };
      nextSBW[warehouseId] = (parseInt(nextSBW[warehouseId] || 0) - exitQty);
      const newStockActual = Object.values(nextSBW).reduce((acc, v) => acc + (parseInt(v) || 0), 0);
      return { ...m, stockByWarehouse: nextSBW, stockActual: newStockActual };
    });
    persistMaterials(nextMaterials4);
    bumpMovementsRefresh();
    return { success: true, message: 'Salida registrada exitosamente', consecutivo };
  };

  // Transfer Operations
  const registerTransfer = async (transferData) => {
    const { 
        materialId, 
        quantity, 
        originWarehouseId, 
        destinationWarehouseId, 
        transferRef, 
        observations, 
        serial, 
        brandName,
        evidencePhotos 
    } = transferData;

    const material = materials.find(m => m.id === parseInt(materialId));
    if (!material) return { success: false, message: 'Material no encontrado' };

    const serialTrim = String(serial || '').trim();
    const isSerial = !!material.requiresSerial || !!serialTrim;

    const qty = isSerial ? 1 : parseInt(quantity);
    if (qty <= 0) return { success: false, message: 'Cantidad inválida' };
    if (isSerial && !serialTrim) return { success: false, message: 'Este material requiere serial. Ingrese el serial a trasladar.' };
    if (isSerial && parseInt(quantity) > 1) return { success: false, message: 'Para ítems seriados, el traslado debe ser de 1 unidad por operación.' };

    const sbw = { ...(material.stockByWarehouse || {}) };
    const originStock = parseInt(sbw[originWarehouseId] || 0);
    if (qty > originStock) {
        return { success: false, message: 'Stock insuficiente en bodega origen' };
    }

    let brandItemId = null;
    let brandResolved = (brandName || '').trim();
    if (isSerial) {
      try {
        const biRes = await api.brandItems.get({ material_code: material.code, serial: serialTrim, warehouse_id: originWarehouseId, include_deleted: 1, limit: 5, order: 'desc' });
        const biList = Array.isArray(biRes?.data) ? biRes.data : (biRes?.data ? [biRes.data] : []);
        const bi = biList.find((x) => String(x?.serial || '').trim() === serialTrim) || biList[0] || null;
        brandItemId = bi?.id || null;
        if (!brandResolved) brandResolved = String(bi?.brand || '').trim();
      } catch (_) {}
      if (!brandItemId) {
        return { success: false, message: 'No se encontró el serial en la bodega origen (brand_items).' };
      }
    }

    let exitOk = false;
    try {
        await api.movements.create({
            type: 'salida',
            material_id: material.id,
            warehouse_id: originWarehouseId,
            quantity: qty,
            notes: `Traslado a ${(warehouses.find(w=>w.id===destinationWarehouseId)?.name || destinationWarehouseId)} - Ref: ${transferRef} - ${observations || ''}`,
            brand_item_id: brandItemId,
            brand: brandResolved || null,
            performed_by_user_id: currentUser?.id,
            performed_by_user_name: currentUser?.name
        });
        exitOk = true;
    } catch (e) {
        console.error('Transfer exit failed', e);
        return { success: false, message: 'Error registrando salida de origen' };
    }

    if (exitOk) {
        try {
            if (isSerial) {
              try {
                await api.brandItems.upsert({
                  id: brandItemId,
                  material_code: material.code,
                  brand: brandResolved || null,
                  serial: serialTrim,
                  status: 'Activo',
                  material_type: material.category || material.type || null,
                  warehouse_id: destinationWarehouseId
                });
              } catch (_) {}
              try {
                await api.serials.upsert({
                  material_code: material.code,
                  serial: serialTrim,
                  warehouse_id: destinationWarehouseId,
                  status: 'Activo'
                });
              } catch (_) {}
            }
            await api.movements.create({
                type: 'entrada',
                material_id: material.id,
                warehouse_id: destinationWarehouseId,
                quantity: qty,
                notes: `Traslado desde ${(warehouses.find(w=>w.id===originWarehouseId)?.name || originWarehouseId)} - Ref: ${transferRef} - ${observations || ''}`,
                brand_item_id: brandItemId,
                serials: isSerial ? [serialTrim] : [],
                brand: brandResolved || null,
                performed_by_user_id: currentUser?.id,
                performed_by_user_name: currentUser?.name
            });
        } catch (e) {
            console.error('Transfer entry failed', e);
            return { success: false, message: 'Error registrando entrada en destino (Salida registrada)' };
        }
    }

    // Upload evidence
    try {
        const photos = Array.isArray(evidencePhotos) ? evidencePhotos.map(p => p?.dataUrl || p).filter(Boolean) : [];
        if (photos.length > 0) {
            // Log evidence for both or just one? Logging for destination seems more relevant for "Arrival"
            await api.evidence.upload({ type: 'entrada', warehouse_id: destinationWarehouseId, entry_ref: String(transferRef || ''), photos });
        }
    } catch (_) {}

    const nextMaterials = materials.map(m => {
        if (m.id !== material.id) return m;
        const nextSBW = { ...(m.stockByWarehouse || {}) };
        nextSBW[originWarehouseId] = (parseInt(nextSBW[originWarehouseId] || 0) - qty);
        nextSBW[destinationWarehouseId] = (parseInt(nextSBW[destinationWarehouseId] || 0) + qty);
        const newStockActual = Object.values(nextSBW).reduce((acc, v) => acc + (parseInt(v) || 0), 0);
        return { ...m, stockByWarehouse: nextSBW, stockActual: newStockActual };
    });
    persistMaterials(nextMaterials);

    const dateNow = new Date();
    const newExit = {
        id: Date.now(),
        type: 'Salida (Traslado)',
        materialId: material.id,
        materialName: material.name,
        quantity: qty,
        date: formatBogotaYmdHms(dateNow),
        user: currentUser?.name || 'admin',
        observations: `Traslado a ${destinationWarehouseId}`,
        warehouseId: originWarehouseId,
        status: 'Completado',
        serial: serialTrim,
        brandName: brandResolved
    };
    const newEntry = {
        id: Date.now() + 1,
        type: 'Entrada (Traslado)',
        materialId: material.id,
        materialName: material.name,
        quantity: qty,
        date: formatBogotaYmdHms(dateNow),
        user: currentUser?.name || 'admin',
        observations: `Traslado desde ${originWarehouseId}`,
        warehouseId: destinationWarehouseId,
        status: 'Completado',
        serial: serialTrim,
        brandName: brandResolved
    };
    
    persistMovements([newEntry, newExit, ...movements]);

    return { success: true, message: 'Traslado exitoso' };
  };

  return {
    materials,
    brandItems,
    people,
    movements,
    assignments,
    warehouses,
    addWarehouse,
    updateWarehouse,
    deleteWarehouse,
    addMaterial,
    updateMaterial,
    deleteMaterial,
    addBrandItem,
    updateBrandItem,
    deleteBrandItem,
    addPerson,
    updatePerson,
    deletePerson,
    registerEntry,
    registerBulkEntry,
    registerAssignment,
    registerReturn,
    registerExit,
    registerTransfer,
    searchMovements,
    movementsRefreshKey,
  };
};
